import { useEffect, useState } from "react";
import { adminApi, setAdminToken, getAdminToken, BUSINESS } from "../lib/api";
import { toast } from "sonner";
import {
  LayoutDashboard,
  FileText,
  MessageSquare,
  LogOut,
  Loader2,
  ShieldCheck,
} from "lucide-react";

export default function Admin() {
  const [authed, setAuthed] = useState(!!getAdminToken());
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await adminApi.post("/admin/login", { password });
      setAdminToken(res.data.token);
      setAuthed(true);
      toast.success("Welcome back");
    } catch (err) {
      toast.error("Wrong password");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    setAdminToken("");
    setAuthed(false);
  };

  if (!authed) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4">
        <form
          onSubmit={login}
          data-testid="admin-login-form"
          className="w-full max-w-md rounded-3xl bg-white border border-gray-100 p-8 shadow-lg"
        >
          <div className="inline-flex items-center justify-center h-12 w-12 rounded-2xl bg-emerald-900 text-emerald-300">
            <ShieldCheck className="h-6 w-6" />
          </div>
          <h1 className="mt-5 text-2xl font-bold text-emerald-950">Admin Login</h1>
          <p className="mt-1.5 text-sm text-slate-500">
            Enter your admin password to view leads.
          </p>
          <label className="mt-6 block">
            <span className="text-sm font-semibold text-emerald-950">Password</span>
            <input
              type="password"
              data-testid="admin-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1.5 w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-emerald-950 focus:border-emerald-400 focus:ring-0 outline-none"
            />
          </label>
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-btn"
            className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-emerald-900 text-white font-semibold text-sm hover:bg-emerald-800 disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Sign in
          </button>
        </form>
      </div>
    );
  }

  return <AdminDashboard onLogout={logout} />;
}

function AdminDashboard({ onLogout }) {
  const [tab, setTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [contacts, setContacts] = useState([]);

  const load = async () => {
    try {
      const [s, q, c] = await Promise.all([
        adminApi.get("/admin/stats"),
        adminApi.get("/admin/quotes"),
        adminApi.get("/admin/contacts"),
      ]);
      setStats(s.data);
      setQuotes(q.data);
      setContacts(c.data);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Session expired");
        onLogout();
      } else {
        toast.error("Failed to load data");
      }
    }
  };

  useEffect(() => {
    load();
  }, []);

  const updateStatus = async (id, status) => {
    try {
      await adminApi.patch(`/admin/quotes/${id}?status=${status}`);
      toast.success("Updated");
      load();
    } catch (err) {
      toast.error("Update failed");
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-[240px_1fr] bg-slate-100">
      <aside className="bg-emerald-950 text-emerald-100 md:min-h-screen p-6 flex flex-col">
        <div className="flex items-center gap-2.5 mb-8">
          <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl overflow-hidden ring-1 ring-emerald-500/30 shadow-md">
            <img
              src={BUSINESS.logoUrl}
              alt="Sixty Finance"
              width="88"
              height="88"
              className="h-full w-full object-cover"
              style={{ imageRendering: "-webkit-optimize-contrast" }}
            />
          </span>
          <div>
            <div className="text-white font-bold text-sm">Sixty Admin</div>
            <div className="text-xs text-emerald-300/80 italic">Always by your side</div>
          </div>
        </div>
        <nav className="space-y-1 text-sm">
          <SidebarBtn active={tab === "dashboard"} onClick={() => setTab("dashboard")} icon={LayoutDashboard} label="Dashboard" testid="admin-tab-dashboard" />
          <SidebarBtn active={tab === "quotes"} onClick={() => setTab("quotes")} icon={FileText} label={`Quotes (${quotes.length})`} testid="admin-tab-quotes" />
          <SidebarBtn active={tab === "contacts"} onClick={() => setTab("contacts")} icon={MessageSquare} label={`Contacts (${contacts.length})`} testid="admin-tab-contacts" />
        </nav>
        <button
          onClick={onLogout}
          data-testid="admin-logout"
          className="mt-auto inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-emerald-200 hover:bg-emerald-900"
        >
          <LogOut className="h-4 w-4" /> Log out
        </button>
      </aside>

      <main className="p-6 md:p-10">
        {tab === "dashboard" && (
          <div data-testid="admin-dashboard">
            <h1 className="text-2xl md:text-3xl font-bold text-emerald-950">Dashboard</h1>
            <p className="mt-1 text-sm text-slate-500">Live pipeline snapshot.</p>
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Stat label="Total quotes" value={stats?.total_quotes ?? "—"} />
              <Stat label="New quotes" value={stats?.new_quotes ?? "—"} />
              <Stat label="Contact msgs" value={stats?.total_contacts ?? "—"} />
              <Stat label="Home loan enquiries" value={stats?.by_type?.home ?? "—"} />
            </div>
            <div className="mt-10 rounded-2xl bg-white border border-gray-200 p-6">
              <h2 className="font-semibold text-emerald-950">By loan type</h2>
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                {["home", "car", "personal", "business"].map((t) => (
                  <div key={t} className="rounded-xl bg-emerald-50 p-4">
                    <div className="text-xs uppercase tracking-widest text-emerald-700 font-semibold">{t}</div>
                    <div className="mt-1 text-2xl font-bold num text-emerald-900">{stats?.by_type?.[t] ?? 0}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === "quotes" && (
          <div data-testid="admin-quotes">
            <h1 className="text-2xl md:text-3xl font-bold text-emerald-950">Quote submissions</h1>
            <div className="mt-6 overflow-x-auto rounded-2xl border border-gray-200 bg-white">
              <table className="min-w-full text-sm">
                <thead className="bg-emerald-50 text-emerald-900 uppercase text-xs tracking-widest">
                  <tr>
                    <th className="text-left p-4">Received</th>
                    <th className="text-left p-4">Name</th>
                    <th className="text-left p-4">Contact</th>
                    <th className="text-left p-4">Type</th>
                    <th className="text-left p-4">Amount</th>
                    <th className="text-left p-4">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {quotes.length === 0 && (
                    <tr><td colSpan={6} className="p-6 text-center text-slate-500">No quotes yet.</td></tr>
                  )}
                  {quotes.map((q) => (
                    <tr key={q.id} className="border-t border-gray-100">
                      <td className="p-4 text-slate-500 num text-xs">{new Date(q.created_at).toLocaleString()}</td>
                      <td className="p-4 font-medium text-emerald-950">{q.full_name}</td>
                      <td className="p-4 text-slate-700"><div>{q.email}</div><div className="num text-xs text-slate-500">{q.phone}</div></td>
                      <td className="p-4 capitalize">{q.loan_type}</td>
                      <td className="p-4 num">{q.loan_amount}</td>
                      <td className="p-4">
                        <select
                          value={q.status}
                          data-testid={`quote-status-${q.id}`}
                          onChange={(e) => updateStatus(q.id, e.target.value)}
                          className="rounded-lg border border-gray-200 px-2 py-1 text-xs"
                        >
                          <option value="new">new</option>
                          <option value="contacted">contacted</option>
                          <option value="quoted">quoted</option>
                          <option value="closed">closed</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "contacts" && (
          <div data-testid="admin-contacts">
            <h1 className="text-2xl md:text-3xl font-bold text-emerald-950">Contact messages</h1>
            <div className="mt-6 space-y-3">
              {contacts.length === 0 && (
                <div className="rounded-2xl bg-white border border-gray-200 p-6 text-center text-slate-500">
                  No contact messages yet.
                </div>
              )}
              {contacts.map((c) => (
                <div key={c.id} className="rounded-2xl bg-white border border-gray-200 p-5">
                  <div className="flex flex-wrap justify-between gap-2">
                    <div>
                      <div className="font-semibold text-emerald-950">{c.full_name}</div>
                      <div className="text-sm text-slate-500">{c.email}{c.phone ? ` · ${c.phone}` : ""}</div>
                    </div>
                    <div className="num text-xs text-slate-500">{new Date(c.created_at).toLocaleString()}</div>
                  </div>
                  {c.subject && <div className="mt-3 font-medium text-emerald-900">{c.subject}</div>}
                  <p className="mt-2 text-sm text-slate-700 whitespace-pre-line">{c.message}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function SidebarBtn({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-left transition-colors ${
        active ? "bg-emerald-500 text-emerald-950 font-semibold" : "hover:bg-emerald-900/60"
      }`}
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-2xl bg-white border border-gray-200 p-6">
      <div className="text-xs uppercase tracking-widest text-emerald-700 font-semibold">{label}</div>
      <div className="mt-2 text-3xl font-bold text-emerald-950 num">{value}</div>
    </div>
  );
}
